import { forwardRef } from 'react';
import { Link as RouterLink, type LinkProps } from 'react-router-dom';
import { publicHref } from '../lib/seo';

/**
 * Drop-in replacement for react-router's `<Link>` that emits the trailing-slash href.
 *
 * Every public route is SERVED at its trailing-slash form — nginx 301s the bare one — and the
 * canonical, sitemap `<loc>` and JSON-LD all use it. But `<Link to="/pricing">` rendered a bare
 * href, so ~2,000 internal links across the prerendered pages pointed at a redirect. 301s pass
 * PageRank, so this was never a ranking bug; it wasted a hop per link and made the in-app URL
 * disagree with the canonical one until the next full page load (click "Pricing" → `/pricing`,
 * refresh → `/pricing/`).
 *
 * Normalising HERE rather than at ~250 call sites is what stops it drifting back: a new
 * `<Link to="/foo">` in any file importing this is correct by construction. `publicHref` leaves
 * client-only paths (`/login`, `/register`, `/app/**`) alone — those are never prerendered as a
 * directory, so nginx serves them from the shell at 200 and a trailing slash would invent a URL
 * form nothing canonicalises to. Non-string `to` (location objects) passes through untouched.
 *
 * Enforced by the "internal hrefs use the trailing-slash form" case in test/seo.spec.ts.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, ...rest }, ref) {
  return <RouterLink ref={ref} to={typeof to === 'string' ? publicHref(to) : to} {...rest} />;
});
